import * as Sentry from '@sentry/browser';
import posthog from 'posthog-js';
import { PRODUCT_EVENTS } from './events.js';

const SENTRY_DSN = (process.env.CAMERAREADY_SENTRY_DSN || '').trim();
const POSTHOG_KEY = (process.env.CAMERAREADY_POSTHOG_KEY || '').trim();
const POSTHOG_HOST = (
  process.env.CAMERAREADY_POSTHOG_HOST || 'https://us.i.posthog.com'
).trim();
const BUILD_ID = (process.env.CAMERAREADY_BUILD_ID || '').trim();
/** GA4 web-stream measurement ID (G-…). Public client id — set via Netlify Builds env. */
const GA4_MEASUREMENT_ID = (
  process.env.CAMERAREADY_GA4_MEASUREMENT_ID || ''
).trim();

const PII_KEY_PATTERN =
  /email|transcript|recording|password|token|authorization|cookie|name|phone|address/i;

function sanitizeProperties(props) {
  if (!props || typeof props !== 'object') return {};
  const out = {};
  for (const [key, value] of Object.entries(props)) {
    if (PII_KEY_PATTERN.test(key)) continue;
    if (typeof value === 'string' && value.length > 500) continue;
    out[key] = value;
  }
  return out;
}

function initSentry() {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: BUILD_ID ? 'production' : 'development',
    release: BUILD_ID || undefined,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }
      if (event.request?.data) delete event.request.data;
      if (event.extra) event.extra = sanitizeProperties(event.extra);
      return event;
    },
  });
}

function initPostHog() {
  if (!POSTHOG_KEY) return false;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false,
    // KEH-7769: landing/UTM measurement requires anonymous $pageview + person UTMs
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: true,
    persistence: 'localStorage+cookie',
    person_profiles: 'always',
  });
  return true;
}

/**
 * Load gtag.js once and fire a default page_view for the current document.
 * Shared via analytics.bundle so every page that already loads the bundle is covered.
 */
function initGa4() {
  if (!GA4_MEASUREMENT_ID || !/^G-[A-Z0-9]+$/i.test(GA4_MEASUREMENT_ID)) {
    return false;
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;

  gtag('js', new Date());
  gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: true,
    anonymize_ip: true,
  });

  const existing = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js"][src*="${GA4_MEASUREMENT_ID}"]`
  );
  if (!existing) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  }

  return true;
}

let posthogReady = false;
let ga4Ready = false;

function captureProductEvent(name, properties) {
  if (!posthogReady) return;
  posthog.capture(name, sanitizeProperties(properties));
}

function surfaceFromPath() {
  const path = window.location.pathname || '/';
  if (path === '/' || path === '/index.html') return 'landing';
  if (path.includes('interview')) return 'interview';
  if (path === '/practice' || path.startsWith('/practice/')) return 'practice';
  if (path.includes('results')) return 'results';
  if (path.includes('checkout-success')) return 'checkout_success';
  if (path.includes('checkout-cancel')) return 'checkout_cancel';
  if (path.includes('library')) return 'library';
  if (path.startsWith('/auth/')) return 'auth';
  if (path.startsWith('/blog')) return 'blog';
  return path.replace(/^\//, '').split('/')[0] || 'unknown';
}

function runObservabilitySelfTest() {
  const surface = surfaceFromPath();
  captureProductEvent(PRODUCT_EVENTS.observability_test, { surface });
  if (SENTRY_DSN) {
    Sentry.captureMessage(`observability_test:${surface}`, 'info');
  }
  if (ga4Ready && typeof window.gtag === 'function') {
    window.gtag('event', 'observability_test', {
      surface,
      event_category: 'cameraready',
    });
  }
}

function maybeRunSelfTest() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cr_obs_test') === '1') runObservabilitySelfTest();
  } catch (_) {}
}

initSentry();
posthogReady = initPostHog();
ga4Ready = initGa4();
maybeRunSelfTest();

window.CRAnalytics = {
  capture: captureProductEvent,
  events: PRODUCT_EVENTS,
  isEnabled: () => posthogReady || Boolean(SENTRY_DSN) || ga4Ready,
  ga4MeasurementId: ga4Ready ? GA4_MEASUREMENT_ID : null,
  runSelfTest: runObservabilitySelfTest,
};

if (typeof window !== 'undefined' && (SENTRY_DSN || posthogReady || ga4Ready)) {
  window.__CR_SENTRY_TEST__ = () => {
    throw new Error('CameraReady Sentry observability test error');
  };
  window.__CR_POSTHOG_TEST__ = () => {
    runObservabilitySelfTest();
  };
  window.__CR_GA4_TEST__ = () => {
    if (!ga4Ready || typeof window.gtag !== 'function') {
      console.warn('[CR] GA4 not configured (CAMERAREADY_GA4_MEASUREMENT_ID)');
      return false;
    }
    window.gtag('event', 'observability_test', {
      surface: surfaceFromPath(),
      event_category: 'cameraready',
    });
    return true;
  };
}
